/**
 * Sidebar Component
 * Navigation sidebar for main layout
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  Dashboard,
  WorkOutline,
  People,
  PersonSearch,
  Assignment,
  Email,
  Settings,
  Assessment,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { useAuth } from '../../features/auth/contexts/AuthContext';

const menuItems = [
  {
    label: 'Dashboard',
    icon: Dashboard,
    path: '/dashboard',
    // Dashboard is visible to all
  },
  {
    label: 'Demand',
    icon: WorkOutline,
    requiredPrivileges: ['DEMANDREQVIEW', 'CANDPIPVIEW', 'PROSERCHVIEW'],
    children: [
      { label: 'Demand List', path: '/demands', requiredPrivilege: 'DEMANDREQVIEW' },
      { label: 'Profile Search', path: '/profile-search', requiredPrivilege: 'PROSERCHVIEW' }
    ]
  },
  {
    label: 'Associates',
    icon: People,
    path: '/associates',
    requiredPrivilege: 'EMPVIEW'
  },
  // {
  //   label: 'Roles',
  //   icon: Assignment,
  //   path: '/roles',
  //   requiredPrivilege: 'ROLEVIEW'
  // },
  // {
  //   label: 'Email Templates',
  //   icon: Email,
  //   path: '/email-templates',
  //   requiredPrivilege: 'EMAILTEMPLATEVIEW'
  // },
  {
    label: 'Reports',
    icon: Assessment,
    requiredPrivileges: ['CUSTOMREPORTVIEW', 'LOGVIEW', 'DEMREPVIEW'],
    children: [
      { label: 'Custom Reports', path: '/reports', requiredPrivilege: 'CUSTOMREPORTVIEW' },
      { label: 'Login Activity History', path: '/login-activity', requiredPrivilege: 'LOGVIEW' },
      { label: 'Demand Report', path: '/demand-report', requiredPrivilege: 'DEMREPVIEW' }
    ]
  },
  {
    label: 'Settings',
    icon: Settings,
    path: '/settings',
    requiredPrivileges: ['APPCONFIGVIEW','ROLEVIEW', 'EMAILTEMPLATEVIEW'],
    children: [
      {label: 'Application Configuration', path: '/settings',requiredPrivilege: 'APPCONFIGVIEW'},
      { label: 'Roles', path: '/roles', requiredPrivilege: 'ROLEVIEW' },
      { label: 'Email Templates', path: '/email-templates', requiredPrivilege: 'EMAILTEMPLATEVIEW' }
    ]
  }
];

const Sidebar = ({ minimized = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, cookies } = useAuth();
  const [openMenus, setOpenMenus] = useState({});

  // Get user privileges from cookies (matching old frontend behavior)
  const privileges = cookies.UserPrivileges || user?.privileges || [];
  const isSuperAdmin = user?.roleName?.toLowerCase().includes('super admin');

  const handleMenuClick = (item) => {
    if (item.children) {
      if (!minimized) {
      setOpenMenus(prev => ({
        ...prev,
        [item.label]: !prev[item.label]
      }));
      }
    } else {
      navigate(item.path);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };
 
  const isChildActive = (children) => {
    if (!children) return false;
    return children.some(child => location.pathname === child.path);
  };

  // Check if user has required privilege
  const hasPrivilege = (requiredPrivilege) => {
    if (!requiredPrivilege) return true;
    if (isSuperAdmin) return true;
    return Array.isArray(privileges) && privileges.includes(requiredPrivilege);
  };

  // Check if user has any of the required privileges
  const hasAnyPrivilege = (requiredPrivileges) => {
    if (!requiredPrivileges || requiredPrivileges.length === 0) return true;
    if (isSuperAdmin) return true;
    return Array.isArray(privileges) && requiredPrivileges.some(priv => privileges.includes(priv));
  };

  // Filter menu items based on privileges
  const visibleMenuItems = menuItems.filter(item => {
    if (item.requiredPrivilege) {
      return hasPrivilege(item.requiredPrivilege);
    }
    if (item.requiredPrivileges) {
      return hasAnyPrivilege(item.requiredPrivileges);
    }
    return true;
  });

  // Filter children based on privileges
  const getVisibleChildren = (children) => {
    if (!children) return null;
    return children.filter(child => {
      if (child.requiredPrivilege) {
        return hasPrivilege(child.requiredPrivilege);
      }
      return true;
    });
  };

  return (
    <Box sx={{ py: 2, px: minimized ? 1 : 2 }}>
      <List sx={{ p: 0 }}>
        {visibleMenuItems.map((item) => {
          const visibleChildren = getVisibleChildren(item.children);
          const hasVisibleChildren = visibleChildren && visibleChildren.length > 0;
          const isMenuActive = !item.children && isActive(item.path);
          const isSubmenuActive = hasVisibleChildren && isChildActive(visibleChildren);

          const menuButton = (
            <ListItemButton
              onClick={() => handleMenuClick(item)}
              selected={isMenuActive || isSubmenuActive}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                minHeight: 48,
                justifyContent: minimized ? 'center' : 'initial',
                px: minimized ? 1.5 : 2.5,
                py: 0.75,
                color: '#575962',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white'
                  },
                  '& .MuiListItemText-primary': {
                    fontWeight: 600
                  }
                },
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.03)',
                  '& .MuiListItemIcon-root': {
                    color: '#4d7cfe'
                  },
                  '& .MuiListItemText-primary': {
                    color: '#575962',
                    fontWeight: 600
                  }
                },
                '&.Mui-selected:hover': {
                  '& .MuiListItemIcon-root': {
                    color: 'white'
                  },
                  '& .MuiListItemText-primary': {
                    color: 'white',
                    fontWeight: 600
                      }
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: minimized ? 0 : 2,
                      justifyContent: 'center',
                      color: (isMenuActive || isSubmenuActive) ? 'inherit' : '#a1a2a6',
                      transition: 'color 0.2s ease',
                      fontSize: '1.25rem'
                    }}
                  >
                    <item.icon fontSize="inherit" />
                  </ListItemIcon>
                  {!minimized && (
                <>
                  <ListItemText primary={item.label}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '14px',
                        fontWeight: 400,
                        transition: 'all 0.2s ease'
                      }
                    }}
                  />
                  {hasVisibleChildren && (
                    <Box component="span" sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                      {openMenus[item.label] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          );
 
          return (
            <React.Fragment key={item.label}>
              <ListItem disablePadding>
                {minimized ? (
                  <Tooltip
                    title={item.label}
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: '#1572e8',
                          fontSize: '13px',
                          fontWeight: 500,
                          px: 1.5,
                          py: 0.75,
                          '& .MuiTooltip-arrow': {
                            color: '#1572e8'
                          }
                        }
                      }
                    }}
                  >
                    {menuButton}
                  </Tooltip>
                ) : (
                  menuButton
                )}
              </ListItem>

              {hasVisibleChildren && !minimized && (
                <Collapse in={openMenus[item.label]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {visibleChildren.map((child) => (
                      <ListItemButton
                        key={child.label}
                        sx={{
                          pl: 6,
                          pr: 2.5,
                          py: 1,
                          borderRadius: 1,
                          mx: 1,
                          mb: 0.5,
                          color: '#575962',
                          transition: 'all 0.2s ease',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(77, 124, 254, 0.1)',
                            color: '#4d7cfe',
                            '&:hover': {
                              bgcolor: 'rgba(77, 124, 254, 0.15)',
                            },
                            '& .MuiListItemText-primary': {
                              fontWeight: 600
                            }
                          },
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.03)',
                            '& .MuiListItemText-primary': {
                              color: '#575962',
                              fontWeight: 600
                            }
                          }
                        }}
                        onClick={() => navigate(child.path)}
                        selected={isActive(child.path)}
                      >
                        <ListItemText primary={child.label}
                          primaryTypographyProps={{
                            sx: {
                              fontSize: '13px',
                              fontWeight: 400,
                              transition: 'all 0.2s ease'
                            }
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default Sidebar;

